import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import { TrendingDown, Lightbulb, Zap, AlertTriangle } from 'lucide-react';
import { fmt, fmtCents, fmtCount, fmtPct } from '../utils/formatters';
import LoadingScreen from '../components/LoadingScreen';
import DataEndsNote from '../components/DataEndsNote';
import Change from '../components/Change';

function KpiCard({ label, value, change, isMoney = false }: {
  label: string; value: number | string; change: number; isMoney?: boolean;
}) {
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(74,45,94,0.08)] transition-all duration-300">
      <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">
        {isMoney ? fmt(value as number) : typeof value === 'number' ? fmtCount(value) : value}
      </div>
      {typeof change === 'number' && change !== 0 && (
        <div className="mt-1">
          <Change value={change} />
        </div>
      )}
    </div>
  );
}

export default function InsightsPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/insights?period=month') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/insights?period=month'));
  const [period, setPeriod] = useState('month');
  const [tab, setTab] = useState<'bundles' | 'declining' | 'opportunities'>('bundles');
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const url = `/api/insights?period=${period}`;
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) setData(null); })
      .finally(() => setLoading(false));
  }, [period]);

  const hasData = data && data.kpis && data.kpis.period_revenue.value > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#2D2A2E]">Insights</h1>
          <DataEndsNote dataEnds={data?.data_ends} period={period} />
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm px-3 outline-none w-44">
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">Last 90 Days</option>
        </select>
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24">
          <Lightbulb size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No insights yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">Upload your transaction data and ClearPath will generate daily insights, bundle recommendations, and dead product alerts.</p>
          <button onClick={() => navigate('/settings/data')} className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">Upload Data</button>
        </div>
      ) : data.fits_item_sales === false ? (
        // Invoices or payments, not item sales: no bundles or dead-product
        // advice (it once offered a discounted "bundle" of two invoices).
        <div className="flex flex-col items-center justify-center py-24">
          <Lightbulb size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">Insights are built for item sales</h3>
          <p className="font-sans text-sm text-[#6B6670] text-center max-w-md">{data.notice}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Active Products" value={data.kpis.active_products.value} change={data.kpis.active_products.change} />
            <KpiCard label="Bundle Opportunities" value={data.kpis.bundle_opportunities.value} change={0} />
            <KpiCard label="Declining Products" value={data.kpis.declining_products.value} change={0} />
            <KpiCard label="Period Revenue" value={data.kpis.period_revenue.value} change={data.kpis.period_revenue.change} isMoney />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-[#FAF9F7] rounded-[8px] p-1 w-fit">
            {[
              { id: 'bundles' as const, icon: Zap, label: 'Bundles' },
              { id: 'declining' as const, icon: TrendingDown, label: 'Declining & Dead' },
              { id: 'opportunities' as const, icon: Lightbulb, label: 'Opportunities' },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-sm font-sans font-medium transition-all ${
                  tab === id
                    ? 'bg-white text-[#2D2A2E] shadow-sm'
                    : 'text-[#6B6670] hover:text-[#2D2A2E]'
                }`}
              >
                <Icon size={14} strokeWidth={1.5} />
                {label}
              </button>
            ))}
          </div>

          {/* Bundles Tab */}
          {tab === 'bundles' && (
            <div>
              <h2 className="font-serif text-lg text-[#2D2A2E] mb-2">Products your customers already buy together</h2>
              {data.bundles.length === 0 ? (
                <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
                  <p className="font-sans text-sm text-[#6B6670]">Not enough co-purchase data to generate bundle recommendations yet. More transactions will unlock this feature.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.bundles.map((b: any, i: number) => (
                    <div key={i} className="bg-white border border-[#E6E2DE] rounded-[14px] p-5 shadow-card">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-sans text-base text-[#2D2A2E] font-semibold">{b.pair}</h3>
                          <p className="font-sans text-sm text-[#6B6670] mt-0.5">
                            Sold on the same day {fmtCount(b.days_together)} of the {fmtCount(b.rarer_days)} days {b.rarer_product} sold
                            <span className="text-[#4A2D5E] font-medium"> ({fmtPct(b.attach_rate)})</span>
                          </p>
                          <p className="font-sans text-xs text-[#918B96] mt-0.5">Same day, not the same receipt.</p>
                        </div>
                        <span className={`text-xs font-sans font-medium px-2 py-1 rounded-full whitespace-nowrap ${
                          b.confidence === 'High' ? 'bg-[#2E6B4F]/10 text-[#2E6B4F]'
                            : b.confidence === 'Medium' ? 'bg-[#4A2D5E]/10 text-[#4A2D5E]'
                            : 'bg-[#918B96]/10 text-[#6B6670]'
                        }`}>{b.confidence}</span>
                      </div>
                      <div className="border-t border-[#E6E2DE] pt-3 mt-2">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide mb-0.5">Combined Price</div>
                            <div className="text-sm font-sans font-semibold text-[#2D2A2E] tabular-nums">{fmtCents(b.combined_price)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide mb-0.5">Suggested Bundle</div>
                            <div className="text-sm font-sans font-semibold text-[#2E6B4F] tabular-nums">{fmtCents(b.suggested_price)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide mb-0.5">Discount</div>
                            <div className="text-sm font-sans font-semibold text-[#2D2A2E] tabular-nums">{fmtCents(b.discount)} off</div>
                          </div>
                        </div>
                        <p className="mt-3 text-[11px] font-sans text-[#7C7589]">
                          Dollar impact estimates for bundles live in your Daily Brief, where they&rsquo;re computed from margins and real co-purchase days.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Declining & Dead Tab */}
          {tab === 'declining' && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={20} strokeWidth={1.5} className="text-[#B54A4A]" />
                <h3 className="font-serif text-lg text-[#2D2A2E]">Dead Product Alerts</h3>
              </div>
              {data.dead_products.length === 0 ? (
                <p className="font-sans text-sm text-[#6B6670]">No declining products detected. All your products are performing consistently.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                        {['Product', 'Status', 'Current Revenue', 'Previous Revenue', 'Change'].map((h) => (
                          <th key={h} className={`p-4 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans ${h === 'Product' || h === 'Status' ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.dead_products.map((p: any) => (
                        <tr key={p.name} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7] transition-colors">
                          <td className="p-4 text-sm font-sans text-[#2D2A2E] font-medium">{p.name}</td>
                          <td className="p-4">
                            <span className={`text-xs font-sans px-2 py-0.5 rounded-full ${
                              p.status === 'gone' ? 'bg-[#B54A4A]/10 text-[#B54A4A]' : 'bg-[#F0EBF5] text-[#4A2D5E]'
                            }`}>
                              {p.status === 'gone' ? 'No Sales' : 'Declining'}
                            </span>
                          </td>
                          <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(p.current_revenue)}</td>
                          <td className="p-4 text-sm font-sans text-[#6B6670] text-right tabular-nums">{fmt(p.previous_revenue)}</td>
                          <td className="p-4 text-right">
                            <Change value={p.change} size="sm" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Opportunities Tab */}
          {tab === 'opportunities' && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb size={20} strokeWidth={1.5} className="text-[#4A2D5E]" />
                <h3 className="font-serif text-lg text-[#2D2A2E]">Growth Opportunities</h3>
              </div>
              <div className="space-y-4">
                {data.bundles.length > 0 && (
                  <button onClick={() => setTab('bundles')} className="w-full text-left p-4 bg-[#FAF9F7] rounded-[8px] hover:bg-[#F2F0ED] transition-colors group">
                    <h4 className="font-sans text-sm text-[#2D2A2E] font-medium mb-1 flex items-center gap-2">
                      Bundle Pricing Strategy
                      <span className="text-xs text-[#4A2D5E] opacity-0 group-hover:opacity-100 transition-opacity">View bundles →</span>
                    </h4>
                    <p className="font-sans text-sm text-[#6B6670]">
                      You have <span className="font-medium text-[#4A2D5E]">{data.bundles.length} strong bundle opportunities</span>. Offering these as combos with a small discount can lift average ticket size.
                    </p>
                  </button>
                )}
                {data.dead_products.length > 0 && (
                  <button onClick={() => setTab('declining')} className="w-full text-left p-4 bg-[#FAF9F7] rounded-[8px] hover:bg-[#F2F0ED] transition-colors group">
                    <h4 className="font-sans text-sm text-[#2D2A2E] font-medium mb-1 flex items-center gap-2">
                      Menu Optimization
                      <span className="text-xs text-[#B54A4A] opacity-0 group-hover:opacity-100 transition-opacity">View declining →</span>
                    </h4>
                    <p className="font-sans text-sm text-[#6B6670]">
                      <span className="font-medium text-[#B54A4A]">{data.dead_products.length} product{data.dead_products.length > 1 ? 's are' : ' is'} declining significantly</span>. Consider removing or repositioning {data.dead_products.slice(0, 2).map((d: any) => d.name).join(' and ')}.
                    </p>
                  </button>
                )}
                <div className="p-4 bg-[#FAF9F7] rounded-[8px]">
                  <h4 className="font-sans text-sm text-[#2D2A2E] font-medium mb-1">Upsell Training</h4>
                  <p className="font-sans text-sm text-[#6B6670]">
                    With {data.kpis.active_products.value} active products, there's room to grow order value through staff training on suggestive selling.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}
